import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
    IBalanceHistoryResponse, IBalanceInfo,
    IBalanceTransaction,
    ICompaniesBalance,
    ICompanyWithBalance
} from "../../../models/iCompanies";
import {
    fetchCheckCompanyById, fetchCreatePayment,
    fetchGetAllCompanies,
    fetchGetBalanceHistory,
    fetchGetBalanceInfo,
    fetchUpdateCompany,
    fetchUpdateCompanyBalance,
    fetchUpdateCompanyTariff
} from "./actions";
import {ICreatePaymentResponse} from "../../../models/iPayment";
import {fetchBillingStatus} from "../../billing/model/actions";

interface IBalanceHistoryState {
    items: IBalanceTransaction[];
    total: number;
    page: number;
    limit: number;
    pages: number;
    isLoading: boolean;
    hasMore: boolean;
}

interface IPaymentState {
    isCreating: boolean;
    lastPayment: ICreatePaymentResponse | null;
    error: string | null;
}

interface ICompaniesState {
    list: ICompanyWithBalance[];
    isLoading: boolean;
    current: ICompanyWithBalance | null;
    balanceHistory: IBalanceHistoryState;
    balanceInfo: IBalanceInfo | null;
    balanceInfoLoading: boolean;
    payment: IPaymentState;
}

const handlePending = (state: ICompaniesState) => {
    state.isLoading = true;
};

const handleRejected = (state: ICompaniesState) => {
    state.isLoading = false;
};

const initialHistoryState: IBalanceHistoryState = {
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    pages: 0,
    isLoading: false,
    hasMore: false,

};

const initialPaymentState: IPaymentState = {
    isCreating: false,
    lastPayment: null,
    error: null,
};

const initialState: ICompaniesState = {
    list: [],
    isLoading: false,
    current: null,
    balanceHistory: initialHistoryState,
    balanceInfo: null,
    balanceInfoLoading: false,
    payment: initialPaymentState,
};

export const CompaniesSlice = createSlice({
    name: "companies",
    initialState,
    reducers: {
        setCompanies: (state, action: PayloadAction<ICompanyWithBalance[]>) => {
            state.list = action.payload;
        },
        setCurrentCompany: (state, action: PayloadAction<ICompanyWithBalance | null>) => {
            state.current = action.payload;
        },
        resetCurrentCompany: (state) => {
            state.current = null;
        },
        resetBalanceHistory: (state) => {
            state.balanceHistory = initialHistoryState;
        },
        resetPayment: (state) => {
            state.payment = initialPaymentState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGetAllCompanies.fulfilled, (state, action: PayloadAction<ICompanyWithBalance[]>) => {
                state.list = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchCheckCompanyById.fulfilled, (state, action: PayloadAction<ICompanyWithBalance>) => {
                state.current = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchUpdateCompany.fulfilled, (state, action: PayloadAction<ICompanyWithBalance>) => {
                state.current = action.payload;
                state.isLoading = false;
            })
            .addCase(fetchUpdateCompanyBalance.fulfilled, (state, action: PayloadAction<ICompaniesBalance>) => {
                state.list = [...state.list.map(company => company.id === action.payload.company_id ? {
                    ...company,
                    balance: action.payload
                } : company)];
                state.isLoading = false;
            })
            .addCase(fetchGetAllCompanies.pending, handlePending)
            .addCase(fetchGetAllCompanies.rejected, handleRejected)
            .addCase(fetchCheckCompanyById.pending, handlePending)
            .addCase(fetchCheckCompanyById.rejected, handleRejected)
            .addCase(fetchUpdateCompany.pending, handlePending)
            .addCase(fetchUpdateCompany.rejected, handleRejected)
            .addCase(fetchUpdateCompanyBalance.pending, handlePending)
            .addCase(fetchUpdateCompanyBalance.rejected, handleRejected)
            .addCase(fetchGetBalanceHistory.pending, (state, action) => {
                if (action.meta.arg.page === 1) {
                    state.balanceHistory.items = [];
                }
                state.balanceHistory.isLoading = true;
            })
            .addCase(fetchGetBalanceHistory.rejected, (state, action) => {
                // ✅ Тихо игнорируем — это StrictMode cleanup или смена компании
                if (
                    action.payload === 'cancelled' ||
                    action.meta.aborted ||
                    action.error?.name === 'AbortError'
                ) {
                    // Если отменили первую страницу — убираем загрузку, не трогаем items
                    state.balanceHistory.isLoading = false;
                    return;
                }
                state.balanceHistory.isLoading = false;
            })
            .addCase(
                fetchGetBalanceHistory.fulfilled,
                (state, action: PayloadAction<IBalanceHistoryResponse>) => {
                    const {items, total, page, limit, pages} = action.payload;
                    state.balanceHistory.items =
                        page === 1 ? items : [...state.balanceHistory.items, ...items];
                    state.balanceHistory.total = total;
                    state.balanceHistory.page = page;
                    state.balanceHistory.limit = limit;
                    state.balanceHistory.pages = pages;
                    state.balanceHistory.hasMore = page < pages;
                    state.balanceHistory.isLoading = false;
                },
            )
            .addCase(fetchUpdateCompanyTariff.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchUpdateCompanyTariff.fulfilled, (state, action) => {
                state.isLoading = false;
                const updated = action.payload;
                const idx = state.list.findIndex((c) => c.id === updated.id);
                if (idx !== -1) {
                    state.list[idx] = {
                        ...state.list[idx],
                        companyTariff: updated.companyTariff,  // ← было: tariff_id, tariff, discount
                    };
                }
            })
            .addCase(fetchUpdateCompanyTariff.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchGetBalanceInfo.pending, (state) => {
                state.balanceInfoLoading = true;
            })
            .addCase(fetchGetBalanceInfo.fulfilled, (state, action) => {
                state.balanceInfoLoading = false;
                state.balanceInfo = action.payload;
            })
            .addCase(fetchGetBalanceInfo.rejected, (state) => {
                state.balanceInfoLoading = false;
            })
            // billing:status_changed перезапрашивает тот же /companies/balance-info в стор billing —
            // карточка баланса читает отсюда, поэтому кладём ответ и сюда (без второго запроса).
            .addCase(fetchBillingStatus.fulfilled, (state, action) => {
                if (state.balanceInfo) {
                    state.balanceInfo = {...state.balanceInfo, ...action.payload} as IBalanceInfo;
                }
            })
            .addCase(fetchCreatePayment.pending, (state) => {
                state.payment.isCreating = true;
                state.payment.error = null;
                state.payment.lastPayment = null;
            })
            .addCase(fetchCreatePayment.fulfilled, (state, action) => {
                state.payment.isCreating = false;
                state.payment.lastPayment = action.payload;
            })
            .addCase(fetchCreatePayment.rejected, (state, action) => {
                state.payment.isCreating = false;
                state.payment.error = action.payload as string ?? 'Ошибка создания платежа';
            });
    },
});

export const {
    setCompanies,
    setCurrentCompany,
    resetCurrentCompany,
    resetBalanceHistory,
    resetPayment,
} = CompaniesSlice.actions;
export default CompaniesSlice.reducer;
